'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Mail, Shield, HelpCircle, AlertCircle } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

const SUPPORT_EMAIL = 'naijaexplorer@aol.com';

const SUPPORT_TOPICS = [
  {
    icon: HelpCircle,
    title: 'General Support',
    description: 'Questions about using Naija Explorer, account setup, or child profiles.',
    email: SUPPORT_EMAIL,
  },
  {
    icon: Shield,
    title: 'Privacy & Data Requests',
    description: 'Requests to access, correct, or delete your account data or child progress data.',
    email: SUPPORT_EMAIL,
  },
  {
    icon: AlertCircle,
    title: 'Report a Problem',
    description: 'Report a technical issue, inappropriate content, or a safety concern.',
    email: SUPPORT_EMAIL,
  },
];

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Minimal header */}
      <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Link
            href="/"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px] min-w-[44px] justify-center sm:justify-start sm:min-w-0 sm:px-2"
            aria-label="Back to home"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Home</span>
          </Link>
          <div className="flex items-center gap-2 ml-1">
            <AppLogo size={26} />
            <span className="font-semibold text-sm text-foreground">Naija Explorer</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Title block */}
        <div className="mb-10">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Mail size={18} className="text-primary" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Contact &amp; Support</h1>
          </div>
          <p className="text-muted-foreground text-sm leading-relaxed">
            We&apos;re here to help. Choose the topic that best matches your enquiry below.
          </p>
        </div>

        {/* Support topics */}
        <div className="space-y-4 mb-10">
          {SUPPORT_TOPICS?.map((topic, i) => {
            const TopicIcon = topic?.icon;
            return (
              <div key={i} className="card-base p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <TopicIcon size={18} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-semibold text-foreground mb-1">
                      {topic?.title}
                    </h2>
                    <p className="text-sm text-muted-foreground mb-2 leading-relaxed">
                      {topic?.description}
                    </p>
                    <p className="text-sm">
                      <span className="text-muted-foreground">Email: </span>
                      <a href={`mailto:${topic?.email}`} className="text-primary hover:underline font-medium">{topic?.email}</a>
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Response time notice */}
        <div className="p-4 rounded-xl bg-muted/40 border border-border mb-10">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Response time:</strong> We aim to respond to all enquiries within 2–3 business days. 
            For urgent safety concerns, please include &quot;URGENT&quot; in your subject line.
          </p>
        </div>

        {/* FAQ section */}
        <div className="mb-10">
          <h2 className="text-lg font-bold text-foreground mb-5">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              {
                q: 'How do I add a child profile?',
                a: 'After signing in to your parent account, go to the Explorer Profiles page. You can add up to multiple child profiles, each with their own name, age group, and avatar.',
              },
              {
                q: 'Can my child use Naija Explorer without me?',
                a: 'Yes — once you have set up a child profile, your child can select their profile and start learning. All content is age-appropriate and ad-free. We recommend parents periodically review progress via the Parent Dashboard.',
              },
              {
                q: 'How do I reset my password?',
                a: 'On the sign-in page, click "Forgot password?" and enter your email address. You will receive a reset link within a few minutes.',
              },
              {
                q: 'How do I delete my account or a child profile?',
                a: 'To delete a child profile or your entire account, please contact us at the support email above. We will process your request within 30 days.',
              },
              {
                q: 'Is Naija Explorer free?',
                a: 'Please check the current pricing information on the main website or contact us for details.',
              },
              {
                q: 'What devices does Naija Explorer work on?',
                a: 'Naija Explorer works on any modern web browser on phones, tablets, and computers. It can also be installed as a Progressive Web App (PWA) on your home screen for an app-like experience.',
              },
            ]?.map((item, i) => (
              <details key={i} className="group card-base overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none font-medium text-foreground text-sm hover:bg-muted/30 transition-colors min-h-[52px]">
                  <span>{item?.q}</span>
                  <span className="ml-3 flex-shrink-0 text-muted-foreground group-open:rotate-180 transition-transform duration-200">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </summary>
                <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">
                  {item?.a}
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Footer nav */}
        <div className="pt-6 border-t border-border flex flex-wrap gap-4 text-sm">
          <Link href="/privacy" className="text-primary hover:underline font-medium">Privacy Policy</Link>
          <Link href="/terms" className="text-primary hover:underline font-medium">Terms of Use</Link>
          <Link href="/" className="text-muted-foreground hover:text-foreground">← Back to Naija Explorer</Link>
        </div>
      </main>
    </div>
  );
}
